import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, CircularProgress, IconButton, Stack, TextField, Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useFetch from '../../../services/useFetch';
import { tokens } from '../../../theme';

function blankRow() {
  return { id: null, name: '', amount: '', measurement: '', type: 'regular' };
}

function blankBatchRow(oz = '') {
  return {
    id: null,
    name: 'Production batch',
    amount: oz,
    measurement: 'oz',
    type: 'batch',
  };
}

function rowsFromProduct(product) {
  if (!product?.requiresSupplies) return [blankRow()];
  const lines = product.ingredients || [];
  if (!lines.length) {
    return [blankBatchRow(parseBottleOz(product)), blankRow()];
  }
  return lines.map((ing) => ({
    id: ing.id,
    name: ing.name || '',
    amount: ing.amount ?? '',
    measurement: ing.measurement || '',
    type: ing.type === 'batch' ? 'batch' : 'regular',
  }));
}

function parseBottleOz(product) {
  const text = `${product?.variant || ''} ${product?.itemName || ''}`;
  const m = text.match(/(\d+(?:\.\d+)?)\s*oz\b/i);
  return m ? m[1] : '';
}

function recipeFingerprint(product) {
  if (!product) return '';
  const lines = (product.ingredients || []).map((ing) => ([
    ing.id ?? '',
    ing.name || '',
    ing.amount ?? '',
    ing.measurement || '',
    ing.type || 'regular',
  ].join(':')));
  return `${Boolean(product.requiresSupplies)}|${lines.join('|')}`;
}

function ensureMadeRows(prevRows, product) {
  const oz = parseBottleOz(product);
  const hasBatch = prevRows.some((r) => r.type === 'batch');
  if (hasBatch) {
    return prevRows.map((r) => (
      r.type === 'batch' && (r.amount === '' || r.amount == null) && oz
        ? { ...r, amount: oz }
        : r
    ));
  }
  const supplies = prevRows.filter((r) => r.type !== 'batch' && (r.id != null || r.name));
  return [blankBatchRow(oz), ...(supplies.length ? supplies : [blankRow()])];
}

/**
 * Toggle Made/Whole and edit raw recipe ingredients on a product (incl. Shopify imports).
 */
export default function ProductRecipeEditor({ product, saving, message, onSave }) {
  const { data: ingredients = [] } = useFetch('api/ingredients', false);
  const [requiresSupplies, setRequiresSupplies] = useState(Boolean(product?.requiresSupplies));
  const [rows, setRows] = useState(() => rowsFromProduct(product));
  const [localError, setLocalError] = useState('');

  const fingerprint = recipeFingerprint(product);

  useEffect(() => {
    setRequiresSupplies(Boolean(product?.requiresSupplies));
    setRows(rowsFromProduct(product));
    setLocalError('');
  }, [product?.itemId, fingerprint]);

  const options = useMemo(
    () => (ingredients || [])
      .filter((ing) => String(ing.category || '').toLowerCase() !== 'batch')
      .map((ing) => ({
        id: ing.ingredientId,
        label: ing.ingredient,
        measurement: ing.recipeUoM || ing.parUoM || '',
      })),
    [ingredients],
  );

  const dirty = useMemo(() => {
    const wasMade = Boolean(product?.requiresSupplies);
    if (requiresSupplies !== wasMade) return true;
    if (!requiresSupplies) return false;

    const original = (product.ingredients || []).map((ing) => ({
      id: ing.id,
      amount: Number(ing.amount),
      measurement: ing.measurement || '',
      type: ing.type === 'batch' ? 'batch' : 'regular',
    }));
    const current = rows
      .filter((r) => (r.type === 'batch' ? String(r.name).trim() : r.id != null && r.name))
      .map((r) => ({
        id: r.id,
        amount: Number(r.amount),
        measurement: r.measurement || '',
        type: r.type === 'batch' ? 'batch' : 'regular',
      }));

    if (original.length !== current.length) return true;
    return current.some((row, i) => (
      String(row.id) !== String(original[i].id)
      || row.amount !== original[i].amount
      || row.measurement !== original[i].measurement
      || row.type !== original[i].type
    ));
  }, [product, requiresSupplies, rows]);

  const updateRow = (index, patch) => {
    setLocalError('');
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const handleSave = () => {
    if (requiresSupplies) {
      const batch = rows.find((r) => r.type === 'batch');
      if (!batch || !(Number(batch.amount) > 0)) {
        setLocalError('Enter how many oz of production batch go into each bottle, then save.');
        return;
      }
    }

    const payloadIngredients = requiresSupplies
      ? rows
        .filter((r) => {
          if (r.type === 'batch') return String(r.name).trim() && Number(r.amount) > 0;
          return r.id != null && String(r.name).trim() && Number(r.amount) > 0;
        })
        .map((r) => ({
          id: r.type === 'batch' ? null : r.id,
          name: r.name,
          type: r.type === 'batch' ? 'batch' : 'regular',
          amount: Number(r.amount),
          measurement: r.measurement,
        }))
      : [];
    onSave?.({ requiresSupplies, ingredients: payloadIngredients });
  };

  return (
    <Box
      sx={{
        py: 2.5,
        px: 2.5,
        border: `1px solid ${tokens.border}`,
        borderRadius: 2,
        backgroundColor: tokens.surface,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-start' }}
        gap={2}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography sx={{ fontWeight: 650, fontSize: 18 }}>
            Product type & recipe
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.inkMuted, mt: 0.25 }}>
            Made products use batch liquid (oz) plus jar/label supplies. Pick the specific batch when creating each SKU.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant={!requiresSupplies ? 'contained' : 'outlined'}
            onClick={() => {
              setRequiresSupplies(false);
              setLocalError('');
            }}
            disabled={saving}
          >
            Whole
          </Button>
          <Button
            size="small"
            variant={requiresSupplies ? 'contained' : 'outlined'}
            onClick={() => {
              setRequiresSupplies(true);
              setLocalError('');
              setRows((prev) => ensureMadeRows(prev, product));
            }}
            disabled={saving}
          >
            Made
          </Button>
        </Stack>
      </Stack>

      {!requiresSupplies ? (
        <Typography variant="body2" sx={{ color: tokens.inkMuted, mb: 2 }}>
          Whole products reduce on-hand count only. COGs uses the stored unit cost.
        </Typography>
      ) : (
        <Stack spacing={1.25} sx={{ mb: 2 }}>
          {rows.map((row, index) => {
            if (row.type === 'batch') {
              return (
                <Stack
                  key={`recipe-batch-${index}`}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                >
                  <Box
                    sx={{
                      flex: 1.6,
                      minWidth: 0,
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      border: `1px solid ${tokens.border}`,
                      backgroundColor: tokens.bg,
                    }}
                  >
                    <Typography sx={{ fontWeight: 650, fontSize: 13, color: tokens.ink }}>
                      Production batch
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.inkMuted, mt: 0.25 }}>
                      Not chosen here — select the batch when you create a SKU.
                    </Typography>
                  </Box>
                  <TextField
                    label="Oz per bottle"
                    type="number"
                    size="small"
                    value={row.amount}
                    onChange={(e) => updateRow(index, { amount: e.target.value })}
                    inputProps={{ min: 0, step: 'any' }}
                    required
                    autoFocus={!row.amount}
                    helperText="Required"
                    sx={{ width: { xs: '100%', sm: 140 } }}
                  />
                  <TextField
                    label="UoM"
                    size="small"
                    value={row.measurement || 'oz'}
                    onChange={(e) => updateRow(index, { measurement: e.target.value })}
                    sx={{ width: { xs: '100%', sm: 90 } }}
                  />
                  <IconButton
                    aria-label="Remove batch line"
                    onClick={() => {
                      setRows((prev) => prev.filter((_, i) => i !== index));
                      setLocalError('');
                    }}
                    sx={{ color: tokens.inkMuted }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              );
            }

            const selected = options.find((o) => String(o.id) === String(row.id)) || null;
            return (
              <Stack
                key={`recipe-row-${index}`}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
              >
                <Autocomplete
                  options={options}
                  value={selected}
                  onChange={(_, val) => {
                    updateRow(index, {
                      id: val?.id ?? null,
                      name: val?.label || '',
                      measurement: val?.measurement || row.measurement || '',
                      type: 'regular',
                    });
                  }}
                  getOptionLabel={(o) => o?.label || ''}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  sx={{ flex: 1.6, minWidth: 0 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Jar / label / supply" size="small" />
                  )}
                />
                <TextField
                  label="Amount"
                  type="number"
                  size="small"
                  value={row.amount}
                  onChange={(e) => updateRow(index, { amount: e.target.value })}
                  inputProps={{ min: 0, step: 'any' }}
                  sx={{ width: { xs: '100%', sm: 110 } }}
                />
                <TextField
                  label="UoM"
                  size="small"
                  value={row.measurement}
                  onChange={(e) => updateRow(index, { measurement: e.target.value })}
                  sx={{ width: { xs: '100%', sm: 110 } }}
                />
                <IconButton
                  aria-label="Remove ingredient"
                  onClick={() => {
                    setRows((prev) => (prev.length <= 1 ? [blankRow()] : prev.filter((_, i) => i !== index)));
                    setLocalError('');
                  }}
                  sx={{ color: tokens.inkMuted }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            );
          })}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button
              size="small"
              startIcon={<AddOutlinedIcon />}
              onClick={() => setRows((prev) => [...prev, blankRow()])}
              sx={{ alignSelf: 'flex-start', color: tokens.accent, fontWeight: 650 }}
            >
              Add supply
            </Button>
            {!rows.some((r) => r.type === 'batch') && (
              <Button
                size="small"
                startIcon={<AddOutlinedIcon />}
                onClick={() => setRows((prev) => [blankBatchRow(parseBottleOz(product)), ...prev])}
                sx={{ alignSelf: 'flex-start', color: tokens.accent, fontWeight: 650 }}
              >
                Add production batch (oz)
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save recipe'}
        </Button>
        {(localError || message?.text) && (
          <Typography
            variant="body2"
            sx={{
              color: (localError || message?.type === 'error') ? tokens.danger : tokens.accent,
              fontWeight: 500,
            }}
          >
            {localError || message.text}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
